import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add as AddIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import apiService from '../../services/api';
import { Confirm, DomainTabProps, TabBody, errorText } from './shared';

interface Category { id?: string; name?: string; note?: string }
interface Host { hostName: string; ip: string[] }

const AdvancedTab: React.FC<DomainTabProps> = ({ serviceId, domain, info, onChanged }) => {
  const assigned = new Set((info?.categories || []).map((c) => c.name));
  const [categories, setCategories] = useState<Category[]>([]);
  const [hosts, setHosts] = useState<Host[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);
  const [dialog, setDialog] = useState<{ ip?: boolean; host?: string } | null>(null);
  const [draft, setDraft] = useState({ host: '', ip: '' });
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [deleteHost, setDeleteHost] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, h] = await Promise.allSettled([
        apiService.domainRequest<{ categories: Category[] }>('get', serviceId, '/categories'),
        apiService.domainRequest<{ hosts: Host[] }>('get', serviceId, '/hosts'),
      ]);
      setCategories(c.status === 'fulfilled' ? c.value.categories || [] : []);
      setHosts(h.status === 'fulfilled' ? h.value.hosts || [] : []);
    } finally {
      setLoading(false);
    }
  }, [serviceId]);

  useEffect(() => { load(); }, [load]);

  const toggleCategory = async (c: Category) => {
    setBusy(`cat-${c.name}`);
    setMsg(null);
    try {
      await apiService.domainRequest('post', serviceId, '/categories/assign', { category_id: c.id || '', category_name: c.name || '', assign: !assigned.has(c.name) });
      await onChanged();
    } catch (e) {
      setMsg({ severity: 'error', text: errorText(e) });
    } finally {
      setBusy(null);
    }
  };

  const saveHost = async () => {
    setBusy('host');
    setDialogError(null);
    try {
      await apiService.domainRequest('post', serviceId, dialog?.ip ? '/hosts/ip' : '/hosts', { host: dialog?.host || draft.host, ip_address: draft.ip });
      setDialog(null);
      setDraft({ host: '', ip: '' });
      setMsg({ severity: 'success', text: 'Saved.' });
      await load();
    } catch (e) {
      setDialogError(errorText(e, 'Could not save the host.'));
    } finally {
      setBusy(null);
    }
  };

  const removeIp = async (host: string, ip: string) => {
    setBusy(`ip-${host}-${ip}`);
    try {
      await apiService.domainRequest('delete', serviceId, `/hosts/${encodeURIComponent(host)}/ip/${encodeURIComponent(ip)}`);
      setMsg({ severity: 'success', text: 'IP address removed.' });
      await load();
    } catch (e) {
      setMsg({ severity: 'error', text: errorText(e) });
    } finally {
      setBusy(null);
    }
  };

  const removeHost = async () => {
    if (!deleteHost) return;
    setBusy('host');
    try {
      await apiService.domainRequest('delete', serviceId, `/hosts/${encodeURIComponent(deleteHost)}`);
      setDeleteHost(null);
      setMsg({ severity: 'success', text: 'Host deleted.' });
      await load();
    } catch (e) {
      setDeleteHost(null);
      setMsg({ severity: 'error', text: errorText(e) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <TabBody>
      {msg && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      {loading && <CircularProgress size={20} sx={{ mb: 1 }} />}

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 0 }}>
        <Typography variant="subtitle2" gutterBottom>Categories</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Labels for grouping domains in Synergy. Click to assign or remove.</Typography>
        {categories.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No categories exist in the Synergy account yet. Create them in the Synergy Wholesale system.</Typography>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {categories.map((c) => (
              <Chip
                key={c.id || c.name}
                label={c.name}
                color={assigned.has(c.name) ? 'primary' : 'default'}
                variant={assigned.has(c.name) ? 'filled' : 'outlined'}
                onClick={busy ? undefined : () => toggleCategory(c)}
                disabled={busy === `cat-${c.name}`}
              />
            ))}
          </Box>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box>
            <Typography variant="subtitle2">Registry hosts (glue records)</Typography>
            <Typography variant="caption" color="text.secondary">Name server hosts under {domain}, e.g. ns1.{domain}, with their IP addresses.</Typography>
          </Box>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => { setDialogError(null); setDraft({ host: '', ip: '' }); setDialog({}); }}>Add host</Button>
        </Box>
        <Table size="small">
          <TableHead><TableRow><TableCell>Host</TableCell><TableCell>IP addresses</TableCell><TableCell align="right" /></TableRow></TableHead>
          <TableBody>
            {hosts.length === 0 && <TableRow><TableCell colSpan={3} align="center" sx={{ py: 2, color: 'text.secondary' }}>No registry hosts.</TableCell></TableRow>}
            {hosts.map((h) => (
              <TableRow key={h.hostName}>
                <TableCell sx={{ fontFamily: 'monospace' }}>{h.hostName}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {(h.ip || []).map((ip) => (
                      <Chip key={ip} size="small" label={ip} onDelete={busy ? undefined : () => removeIp(h.hostName, ip)} />
                    ))}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Button size="small" onClick={() => { setDialogError(null); setDraft({ host: h.hostName, ip: '' }); setDialog({ ip: true, host: h.hostName }); }}>Add IP</Button>
                  <Tooltip title="Delete host"><IconButton size="small" color="error" onClick={() => setDeleteHost(h.hostName)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={!!dialog} onClose={busy === 'host' ? undefined : () => setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{dialog?.ip ? `Add IP to ${dialog.host}` : 'Add registry host'}</DialogTitle>
        <DialogContent dividers>
          {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!dialog?.ip && <TextField label="Host" value={draft.host} onChange={(e) => setDraft({ ...draft, host: e.target.value })} placeholder={`ns1.${domain}`} helperText={`Must end in ${domain}`} />}
            <TextField label="IP address" value={draft.ip} onChange={(e) => setDraft({ ...draft, ip: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={busy === 'host'}>Cancel</Button>
          <Button variant="contained" onClick={saveHost} disabled={busy === 'host' || !draft.ip.trim() || (!dialog?.ip && !draft.host.trim())}>{busy === 'host' ? <CircularProgress size={20} /> : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Confirm open={!!deleteHost} busy={busy === 'host'} danger title="Delete this registry host?" text={deleteHost || ''} confirmLabel="Delete" onCancel={() => setDeleteHost(null)} onConfirm={removeHost} />
    </TabBody>
  );
};

export default AdvancedTab;
